import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Truck,
  Shield,
  Clock,
  CheckCircle2,
  ArrowRight,
  Calendar as CalendarIcon,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "0493 74 34 38";
const PHONE_RAW = "+32493743438";
const EMAIL = "info@liftdirect.be";
const BTW = "BE 1029.312.926";
const OPENING_HOURS = "Ma — Za, 7:00 — 20:00";
const COMPANY = "Liftservice De Vos";
const LIFT_IMAGE =
  "https://customer-assets.emergentagent.com/job_839dc5cb-a5eb-4012-9c0b-669989ceb836/artifacts/hkij4gt4_lift%20rood.png";
const LIFT_IMAGE_2 =
  "https://customer-assets.emergentagent.com/job_devos-liftservice/artifacts/lfshq7ss_IMG_4954.jpeg";

const TARIEVEN = [
  {
    key: "oost",
    regio: "Oost-Vlaanderen",
    prijs: "97,50",
    badge: "Thuisbasis",
    omschrijving:
      "Vertrek vanuit Laarne. Snel ter plaatse in heel Oost-Vlaanderen.",
  },
  {
    key: "west",
    regio: "West-Vlaanderen",
    prijs: "102,50",
    badge: null,
    omschrijving:
      "Eerste uur ter plaatse, inclusief opstelling en bediening van de lift.",
  },
  {
    key: "antwerpen",
    regio: "Antwerpen",
    prijs: "107,50",
    badge: null,
    omschrijving:
      "Verhuislift beschikbaar in heel de provincie Antwerpen — efficiënt en veilig.",
  },
  {
    key: "brussel",
    regio: "Brussel",
    prijs: "112,50",
    badge: null,
    omschrijving:
      "Verhuislift in het Brussels Hoofdstedelijk Gewest — vakkundige bediening in stedelijke omgeving.",
  },
];

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative ${className}`}>
    {children}
  </section>
);

const SmallLabel = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.3em] text-zinc-400 uppercase"
  >
    <span className="w-6 h-px bg-[#E62E2D]" />
    {children}
  </div>
);

export default function Landing() {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    naam: "",
    email: "",
    telefoon: "",
    datum: "",
    adres: "",
    regio: "Oost-Vlaanderen",
    bericht: "",
  });

  useEffect(() => {
    document.title = "Liftdirect — Verhuislift in Vlaanderen & Brussel";
  }, []);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.naam || !form.email || !form.telefoon) {
      toast.error("Vul minstens naam, e-mail en telefoon in.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Aanvraag verzonden! We nemen zo snel mogelijk contact op.");
      setForm({
        naam: "",
        email: "",
        telefoon: "",
        datum: "",
        adres: "",
        regio: "Oost-Vlaanderen",
        bericht: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Er ging iets mis. Probeer opnieuw of bel ons.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 overflow-x-hidden">
      {/* ============ STICKY CALL BUTTON ============ */}
      <a
        data-testid="sticky-call-btn"
        href={`tel:${PHONE_RAW}`}
        aria-label={`Bel ${PHONE_DISPLAY}`}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60] group"
      >
        <span className="absolute inset-0 rounded-full bg-[#E62E2D] opacity-60 animate-ping pointer-events-none" />
        <span className="relative inline-flex items-center gap-3 bg-[#E62E2D] hover:bg-[#C81E1D] text-white pl-4 pr-5 py-3 sm:py-3.5 rounded-full shadow-[0_8px_24px_rgba(230,46,45,0.45)] transition-colors">
          <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/15 flex items-center justify-center">
            <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
          </span>
          <span className="font-display tracking-[0.18em] text-sm sm:text-base whitespace-nowrap">
            BEL NU
          </span>
        </span>
      </a>
      {/* ============ HEADER ============ */}
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 backdrop-blur bg-[#050505]/85 border-b border-zinc-900"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <a
            href="#top"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src="/logo.png"
              alt="Liftservice De Vos logo"
              className="w-10 h-10 object-contain"
            />
            <span className="font-display text-xl tracking-wider">
              LIFTSERVICE <span className="text-[#E62E2D]">DE&nbsp;VOS</span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8 text-sm tracking-wide">
            <button
              data-testid="nav-diensten"
              onClick={() => scrollTo("diensten")}
              className="hover:text-[#E62E2D] transition-colors"
            >
              Diensten
            </button>
            <button
              data-testid="nav-tarieven"
              onClick={() => scrollTo("tarieven")}
              className="hover:text-[#E62E2D] transition-colors"
            >
              Tarieven
            </button>
            <button
              data-testid="nav-werkgebied"
              onClick={() => scrollTo("werkgebied")}
              className="hover:text-[#E62E2D] transition-colors"
            >
              Werkgebied
            </button>
            <button
              data-testid="nav-contact"
              onClick={() => scrollTo("contact")}
              className="hover:text-[#E62E2D] transition-colors"
            >
              Contact
            </button>
          </nav>

          <div className="flex items-center gap-2">
            <a
              data-testid="header-phone-link"
              href={`tel:${PHONE_RAW}`}
              className="hidden sm:inline-flex items-center gap-2 px-4 h-10 bg-[#E62E2D] text-white font-medium hover:bg-[#C81E1D] transition-colors btn-red"
            >
              <Phone className="w-4 h-4" />
              <span className="font-display tracking-wider">{PHONE_DISPLAY}</span>
            </a>

            {/* Mobile menu (shadcn dropdown) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="mobile-menu-btn"
                  className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-zinc-800 text-zinc-200"
                  aria-label="Open menu"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#0d0d0d] border-zinc-800 rounded-none text-zinc-100"
              >
                <DropdownMenuItem
                  data-testid="mobile-menu-diensten"
                  onClick={() => scrollTo("diensten")}
                >
                  Diensten
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mobile-menu-tarieven"
                  onClick={() => scrollTo("tarieven")}
                >
                  Tarieven
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mobile-menu-werkgebied"
                  onClick={() => scrollTo("werkgebied")}
                >
                  Werkgebied
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mobile-menu-contact"
                  onClick={() => scrollTo("contact")}
                >
                  Contact
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href={`tel:${PHONE_RAW}`} data-testid="mobile-menu-phone">
                    Bel {PHONE_DISPLAY}
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main id="top">
        {/* ============ HERO ============ */}
        <Section id="hero" className="grain border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <SmallLabel testId="hero-eyebrow">
                Verhuislift · Vertrek vanuit Laarne
              </SmallLabel>

              <h1
                data-testid="hero-heading"
                className="mt-6 font-display text-[44px] leading-[0.95] sm:text-6xl lg:text-[88px] tracking-tight"
              >
                Verhuislift
                <br />
                <span className="text-[#E62E2D]">nodig?</span>{" "}
                <span className="text-zinc-500">Wij rijden uit.</span>
              </h1>

              <p
                data-testid="hero-subtext"
                className="mt-6 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed"
              >
                Professionele verhuislift voor particulieren, verhuisbedrijven en
                aannemers. Veilig, snel ter plaatse en met vaste, transparante
                tarieven in heel Oost-Vlaanderen, West-Vlaanderen, Antwerpen en
                Brussel.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  data-testid="hero-call-button"
                  href={`tel:${PHONE_RAW}`}
                  className="btn-red inline-flex items-center gap-3 h-14 px-6 bg-[#E62E2D] hover:bg-[#C81E1D] text-white font-display tracking-widest text-lg"
                >
                  <Phone className="w-5 h-5" />
                  Bel {PHONE_DISPLAY}
                </a>
                <button
                  data-testid="hero-quote-button"
                  onClick={() => scrollTo("contact")}
                  className="inline-flex items-center gap-2 h-14 px-6 border border-zinc-700 hover:border-[#E62E2D] hover:text-[#E62E2D] transition-colors text-sm tracking-widest uppercase"
                >
                  Vraag offerte aan <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 max-w-lg">
                <Stat label="Vertrek" value="Laarne" testId="hero-stat-1" />
                <Stat label="Eerste uur vanaf" value="€97,50" testId="hero-stat-2" />
                <Stat label="Halfuur extra" value="€45" testId="hero-stat-3" />
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <HeroSlideshow images={[LIFT_IMAGE, LIFT_IMAGE_2]} />
              <div className="hazard-stripes h-3 w-3/4 mt-4 ml-auto" />
            </div>
          </div>

          {/* Marquee */}
          <div className="border-t border-zinc-900 bg-[#0a0a0a]">
            <div className="marquee py-3">
              <div className="marquee-track text-zinc-500 text-sm tracking-[0.3em] uppercase">
                {Array.from({ length: 2 }).map((_, i) => (
                  <span key={i} className="flex items-center">
                    {[
                      "Verhuislift",
                      "Oost-Vlaanderen",
                      "West-Vlaanderen",
                      "Antwerpen",
                      "Brussel",
                      "Snelle service",
                      "Vaste tarieven",
                      "Veilig & verzekerd",
                    ].map((w) => (
                      <span key={`${i}-${w}`} className="flex items-center">
                        <span className="mx-6">{w}</span>
                        <span className="w-1.5 h-1.5 bg-[#E62E2D]" />
                      </span>
                    ))}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* ============ DIENSTEN ============ */}
        <Section id="diensten" className="border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <SmallLabel testId="diensten-eyebrow">Wat we doen</SmallLabel>
                <h2
                  data-testid="diensten-heading"
                  className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
                >
                  Eén lift.
                  <br />
                  <span className="text-[#E62E2D]">Eindeloos</span> handig.
                </h2>
                <p className="mt-6 text-zinc-400 max-w-md">
                  Van zware kasten op een 4e verdieping tot een volledige verhuis
                  zonder kraken in de trap. Wij bedienen de lift, jij focust op
                  de spullen.
                </p>
              </div>

              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
                {[
                  {
                    icon: Truck,
                    title: "Particuliere verhuis",
                    text: "Snel zware en breekbare meubels via raam of balkon naar boven of beneden.",
                    testId: "service-particulier",
                  },
                  {
                    icon: Shield,
                    title: "Voor verhuisbedrijven",
                    text: "Onderaannemer-tarief en flexibele inzet voor uw planning in Vlaanderen & Brussel.",
                    testId: "service-verhuisbedrijven",
                  },
                  {
                    icon: Clock,
                    title: "Levering & bouw",
                    text: "Materialen, ramen, isolatie of meubilair efficiënt op hoogte gebracht.",
                    testId: "service-bouw",
                  },
                  {
                    icon: CheckCircle2,
                    title: "Veilig & verzekerd",
                    text: "Goed onderhouden materiaal, ervaren bediening en correcte papieren.",
                    testId: "service-veilig",
                  },
                ].map((s) => (
                  <div
                    key={s.title}
                    data-testid={s.testId}
                    className="bg-[#0a0a0a] p-7 sm:p-8 hover:bg-[#0e0e0e] transition-colors"
                  >
                    <s.icon className="w-7 h-7 text-[#E62E2D]" strokeWidth={1.5} />
                    <h3 className="mt-5 font-display text-2xl">{s.title}</h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                      {s.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* ============ TARIEVEN ============ */}
        <Section id="tarieven" className="border-b border-zinc-900 bg-[#070707]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div>
                <SmallLabel testId="tarieven-eyebrow">Transparante prijzen</SmallLabel>
                <h2
                  data-testid="tarieven-heading"
                  className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
                >
                  Tarieven<span className="text-[#E62E2D]">.</span>
                </h2>
                <p className="mt-4 text-zinc-400 max-w-xl">
                  Alle prijzen zijn{" "}
                  <span className="text-zinc-200 font-medium">exclusief 21% BTW</span>{" "}
                  en gelden voor het eerste uur ter plaatse. Wij beschikken over{" "}
                  <span className="text-zinc-200 font-medium">twee units</span>{" "}
                  zodat we elke situatie aankunnen.
                </p>
              </div>
              <div className="hidden lg:block">
                <div className="hazard-stripes-thin h-2 w-40" />
              </div>
            </div>

            {/* ============ UNIT 01 ============ */}
            <div className="mt-14 flex items-center gap-4">
              <span className="px-3 py-1 bg-[#E62E2D] text-white text-xs tracking-[0.25em] font-display">
                UNIT 01
              </span>
              <div className="flex-1 h-px bg-zinc-900" />
              <span className="font-display text-zinc-300 text-sm tracking-[0.2em] uppercase">
                Standaard verhuislift · Tot 7de verdieping
              </span>
            </div>
            <p className="mt-3 text-zinc-500 text-sm max-w-2xl">
              Onze grote lift voor verhuizingen via raam, balkon of dakgoot —
              reikt vlot tot de 7de verdieping voor zware lasten en grote
              meubels.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
              {TARIEVEN.map((t) => (
                <article
                  key={t.key}
                  data-testid={`pricing-card-${t.key}`}
                  className="price-card bg-[#0a0a0a] p-8 border-zinc-900 relative flex flex-col"
                >
                  {t.badge && (
                    <span
                      data-testid={`pricing-badge-${t.key}`}
                      className="absolute top-0 right-0 bg-[#E62E2D] text-white text-[10px] tracking-[0.25em] font-display px-3 py-1"
                    >
                      {t.badge}
                    </span>
                  )}
                  <div className="text-zinc-500 text-xs tracking-[0.3em] uppercase">
                    Regio
                  </div>
                  <h3
                    data-testid={`pricing-region-${t.key}`}
                    className="mt-2 font-display text-2xl sm:text-3xl"
                  >
                    {t.regio}
                  </h3>

                  <div className="mt-8 flex items-baseline gap-2">
                    <span className="text-zinc-500 text-2xl font-display">€</span>
                    <span
                      data-testid={`pricing-price-${t.key}`}
                      className="font-display text-6xl text-zinc-50 leading-none"
                    >
                      {t.prijs}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-500 tracking-wider uppercase">
                    Eerste uur ter plaatse · excl. BTW
                  </div>

                  <p className="mt-6 text-sm text-zinc-400 leading-relaxed">
                    {t.omschrijving}
                  </p>

                  <div className="mt-8 pt-6 border-t border-zinc-900 flex items-center justify-between">
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">
                      Extra halfuur
                    </span>
                    <span className="font-display text-xl text-[#E62E2D]">€45</span>
                  </div>
                </article>
              ))}
            </div>

            {/* ============ UNIT 02 — Treklift ============ */}
            <div className="mt-16 flex items-center gap-4">
              <span className="px-3 py-1 bg-[#E62E2D] text-white text-xs tracking-[0.25em] font-display">
                UNIT 02
              </span>
              <div className="flex-1 h-px bg-zinc-900" />
              <span className="font-display text-zinc-300 text-sm tracking-[0.2em] uppercase">
                Treklift · Tot 4–5 verdiepingen
              </span>
            </div>
            <p className="mt-3 text-zinc-500 text-sm max-w-2xl">
              Compacte aanhanger-lift, ideaal voor moeilijk bereikbare plekken:
              <span className="text-zinc-300"> onder garagepoorten</span>, in
              smalle straten, op binnenkoeren of bij beperkte vrije ruimte.
              Reikt vlot tot de 4de à 5de verdieping.
            </p>

            <div
              data-testid="pricing-unit2-grid"
              className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900"
            >
              {[
                {
                  key: "oost",
                  regio: "Oost-Vlaanderen",
                  prijs: "125,00",
                  badge: "Thuisbasis",
                },
                {
                  key: "west",
                  regio: "West-Vlaanderen",
                  prijs: "132,50",
                  badge: null,
                },
                {
                  key: "antwerpen",
                  regio: "Antwerpen",
                  prijs: "140,00",
                  badge: null,
                },
                {
                  key: "brussel",
                  regio: "Brussel",
                  prijs: "147,50",
                  badge: null,
                },
              ].map((t) => (
                <article
                  key={`unit2-${t.key}`}
                  data-testid={`pricing-unit2-card-${t.key}`}
                  className="price-card bg-[#0a0a0a] p-8 border-zinc-900 relative flex flex-col"
                >
                  {t.badge && (
                    <span className="absolute top-0 right-0 bg-[#E62E2D] text-white text-[10px] tracking-[0.25em] font-display px-3 py-1">
                      {t.badge}
                    </span>
                  )}
                  <div className="text-zinc-500 text-xs tracking-[0.3em] uppercase">
                    Regio
                  </div>
                  <h3 className="mt-2 font-display text-2xl sm:text-3xl">
                    {t.regio}
                  </h3>

                  <div className="mt-8 flex items-baseline gap-2">
                    <span className="text-zinc-500 text-2xl font-display">€</span>
                    <span
                      data-testid={`pricing-unit2-price-${t.key}`}
                      className="font-display text-6xl text-zinc-50 leading-none"
                    >
                      {t.prijs}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-500 tracking-wider uppercase">
                    Eerste uur ter plaatse · excl. 21% BTW
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-900 flex items-center justify-between">
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">
                      Extra halfuur
                    </span>
                    <span className="font-display text-xl text-[#E62E2D]">€45</span>
                  </div>
                </article>
              ))}
            </div>

            <div
              data-testid="tarieven-extra-info"
              className="mt-8 grid sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900"
            >
              <InfoBlock
                title="Hoe wordt er gerekend?"
                text="Het eerste uur start bij aankomst ter plaatse. Daarna wordt per begonnen halfuur afgerekend aan €45 excl. 21% BTW."
              />
              <InfoBlock
                title="Welke unit kies ik?"
                text="Geen idee welke lift past? Bel ons gewoon — wij adviseren gratis op basis van adres, verdieping en doorgangen."
              />
            </div>
          </div>
        </Section>

        {/* ============ WERKGEBIED / ABOUT ============ */}
        <Section id="werkgebied" className="border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <SmallLabel testId="werkgebied-eyebrow">Werkgebied</SmallLabel>
              <h2
                data-testid="werkgebied-heading"
                className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
              >
                Vertrek vanuit
                <br />
                <span className="text-[#E62E2D]">Laarne.</span>
              </h2>
              <p className="mt-6 text-zinc-400">
                Vanuit ons centrale vertrekpunt in Laarne bedienen we heel
                Oost-Vlaanderen, West-Vlaanderen, Antwerpen en Brussel. Geen
                ingewikkelde regiotoeslagen — kijk gewoon onze tarieventabel na.
              </p>

              <a
                data-testid="werkgebied-call-link"
                href={`tel:${PHONE_RAW}`}
                className="mt-8 inline-flex items-center gap-3 group"
              >
                <span className="w-12 h-12 bg-[#E62E2D] flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </span>
                <span>
                  <span className="block text-xs tracking-[0.3em] text-zinc-500 uppercase">
                    Bel direct
                  </span>
                  <span className="block font-display text-2xl text-zinc-100 group-hover:text-[#E62E2D] transition-colors">
                    {PHONE_DISPLAY}
                  </span>
                </span>
              </a>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
                {[
                  { label: "Oost-Vlaanderen", price: "€97,50" },
                  { label: "West-Vlaanderen", price: "€102,50" },
                  { label: "Antwerpen", price: "€107,50" },
                  { label: "Brussel", price: "€112,50" },
                ].map((r) => (
                  <div
                    key={r.label}
                    data-testid={`werkgebied-${r.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="bg-[#0a0a0a] p-6 sm:p-8 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-[#E62E2D]" />
                      <span className="font-display text-xl">{r.label}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
                        1e uur
                      </div>
                      <div className="font-display text-lg text-zinc-100">
                        {r.price}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 hazard-stripes-thin h-2 w-full" />
            </div>
          </div>
        </Section>

        {/* ============ CONTACT ============ */}
        <Section id="contact" className="bg-[#070707] border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SmallLabel testId="contact-eyebrow">Contact</SmallLabel>
              <h2
                data-testid="contact-heading"
                className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
              >
                Klus inboeken?
              </h2>
              <p className="mt-6 text-zinc-400 max-w-md">
                Bel ons rechtstreeks voor de snelste planning, of stuur het
                formulier in. We bevestigen je aanvraag zo snel mogelijk.
              </p>

              <div className="mt-10 space-y-5">
                <a
                  data-testid="contact-phone-link"
                  href={`tel:${PHONE_RAW}`}
                  className="flex items-center gap-4 group"
                >
                  <span className="w-12 h-12 border border-zinc-800 group-hover:border-[#E62E2D] flex items-center justify-center transition-colors">
                    <Phone className="w-5 h-5 text-[#E62E2D]" />
                  </span>
                  <div>
                    <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                      Telefoon
                    </div>
                    <div className="font-display text-xl group-hover:text-[#E62E2D] transition-colors">
                      {PHONE_DISPLAY}
                    </div>
                  </div>
                </a>

                <a
                  data-testid="contact-email-link"
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-4 group"
                >
                  <span className="w-12 h-12 border border-zinc-800 group-hover:border-[#E62E2D] flex items-center justify-center transition-colors">
                    <Mail className="w-5 h-5 text-[#E62E2D]" />
                  </span>
                  <div>
                    <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                      E-mail
                    </div>
                    <div className="font-display text-xl group-hover:text-[#E62E2D] transition-colors break-all">
                      {EMAIL}
                    </div>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 border border-zinc-800 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#E62E2D]" />
                  </span>
                  <div>
                    <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                      Vertrek
                    </div>
                    <div
                      data-testid="contact-location"
                      className="font-display text-xl"
                    >
                      Laarne — Oost-Vlaanderen
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 border border-zinc-800 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#E62E2D]" />
                  </span>
                  <div>
                    <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                      Openingsuren
                    </div>
                    <div data-testid="contact-hours" className="font-display text-xl">
                      {OPENING_HOURS}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="lg:col-span-7 bg-[#0a0a0a] border border-zinc-900 p-7 sm:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Naam *">
                  <Input
                    data-testid="contact-input-naam"
                    value={form.naam}
                    onChange={handleChange("naam")}
                    placeholder="Voor- en achternaam"
                    className="bg-[#070707] border-zinc-800 rounded-none h-12 focus-visible:ring-[#E62E2D] focus-visible:border-[#E62E2D]"
                    required
                  />
                </Field>
                <Field label="E-mail *">
                  <Input
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={handleChange("email")}
                    type="email"
                    placeholder="naam@email.be"
                    className="bg-[#070707] border-zinc-800 rounded-none h-12 focus-visible:ring-[#E62E2D] focus-visible:border-[#E62E2D]"
                    required
                  />
                </Field>
                <Field label="Telefoon *">
                  <Input
                    data-testid="contact-input-telefoon"
                    value={form.telefoon}
                    onChange={handleChange("telefoon")}
                    placeholder="0470 12 34 56"
                    className="bg-[#070707] border-zinc-800 rounded-none h-12 focus-visible:ring-[#E62E2D] focus-visible:border-[#E62E2D]"
                    required
                  />
                </Field>
                <Field label="Gewenste datum">
                  <div className="relative">
                    <Input
                      data-testid="contact-input-datum"
                      value={form.datum}
                      onChange={handleChange("datum")}
                      type="date"
                      className="bg-[#070707] border-zinc-800 rounded-none h-12 focus-visible:ring-[#E62E2D] focus-visible:border-[#E62E2D] pr-10"
                    />
                    <CalendarIcon className="w-4 h-4 text-zinc-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </Field>
                <Field label="Adres van de klus">
                  <Input
                    data-testid="contact-input-adres"
                    value={form.adres}
                    onChange={handleChange("adres")}
                    placeholder="Straat, nr, gemeente"
                    className="bg-[#070707] border-zinc-800 rounded-none h-12 focus-visible:ring-[#E62E2D] focus-visible:border-[#E62E2D]"
                  />
                </Field>
                <Field label="Regio">
                  <Select
                    value={form.regio}
                    onValueChange={(v) => setForm((p) => ({ ...p, regio: v }))}
                  >
                    <SelectTrigger
                      data-testid="contact-select-regio"
                      className="bg-[#070707] border-zinc-800 rounded-none h-12 focus:ring-[#E62E2D]"
                    >
                      <SelectValue placeholder="Selecteer regio" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-zinc-800 rounded-none text-zinc-100">
                      <SelectItem value="Oost-Vlaanderen">Oost-Vlaanderen</SelectItem>
                      <SelectItem value="West-Vlaanderen">West-Vlaanderen</SelectItem>
                      <SelectItem value="Antwerpen">Antwerpen</SelectItem>
                      <SelectItem value="Brussel">Brussel</SelectItem>
                      <SelectItem value="Andere">Andere</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Bericht / details">
                  <Textarea
                    data-testid="contact-input-bericht"
                    value={form.bericht}
                    onChange={handleChange("bericht")}
                    rows={5}
                    placeholder="Verdieping, soort meubels, toegang, …"
                    className="bg-[#070707] border-zinc-800 rounded-none focus-visible:ring-[#E62E2D] focus-visible:border-[#E62E2D] resize-none"
                  />
                </Field>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <p className="text-xs text-zinc-500 max-w-sm">
                  Door te verzenden ga je akkoord dat we je gegevens enkel
                  gebruiken om je aanvraag te beantwoorden.
                </p>
                <Button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-red h-12 px-7 bg-[#E62E2D] hover:bg-[#C81E1D] text-white font-display tracking-[0.25em] text-sm rounded-none disabled:opacity-60"
                >
                  {submitting ? "Verzenden…" : "Verstuur aanvraag"}
                </Button>
              </div>
            </form>
          </div>
        </Section>

        {/* ============ FOOTER ============ */}
        <footer className="relative overflow-hidden bg-[#050505]">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
            <span className="font-display text-stroke text-[22vw] leading-none tracking-tighter whitespace-nowrap">
              DE VOS
            </span>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-3">
                <img
                  src="/logo.png"
                  alt="Liftservice De Vos logo"
                  className="w-10 h-10 object-contain"
                />
                <span className="font-display text-lg tracking-wider">
                  LIFTSERVICE DE VOS
                </span>
              </div>
              <p className="mt-4 text-sm text-zinc-500 max-w-xs">
                Verhuislift met vaste tarieven in Vlaanderen & Brussel. Vertrek
                vanuit Laarne.
              </p>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                Contact
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <a
                    data-testid="footer-phone-link"
                    href={`tel:${PHONE_RAW}`}
                    className="inline-flex items-center gap-2 hover:text-[#E62E2D]"
                  >
                    <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
                  </a>
                </li>
                <li>
                  <a
                    data-testid="footer-email-link"
                    href={`mailto:${EMAIL}`}
                    className="inline-flex items-center gap-2 hover:text-[#E62E2D] break-all"
                  >
                    <Mail className="w-4 h-4" /> {EMAIL}
                  </a>
                </li>
                <li className="inline-flex items-center gap-2 text-zinc-400">
                  <MapPin className="w-4 h-4 text-[#E62E2D]" /> Laarne, België
                </li>
                <li className="inline-flex items-center gap-2 text-zinc-400">
                  <Clock className="w-4 h-4 text-[#E62E2D]" /> {OPENING_HOURS}
                </li>
              </ul>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                Snel naar
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <button onClick={() => scrollTo("diensten")} className="hover:text-[#E62E2D]">
                    Diensten
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo("tarieven")} className="hover:text-[#E62E2D]">
                    Tarieven
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo("contact")} className="hover:text-[#E62E2D]">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                Website
              </div>
              <ul className="mt-3 space-y-2 text-sm text-zinc-400">
                <li>www.liftdirect.be</li>
                <li data-testid="footer-btw">BTW: {BTW}</li>
                <li className="text-zinc-600 text-xs">
                  Alle prijzen excl. BTW
                </li>
              </ul>
            </div>
          </div>

          <div className="relative hazard-stripes-thin h-2" />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between text-xs text-zinc-600">
            <div data-testid="footer-copyright">
              © {new Date().getFullYear()} {COMPANY}. Alle rechten voorbehouden.
            </div>
            <div className="mt-2 sm:mt-0">Made with steel in Laarne.</div>
          </div>
        </footer>
      </main>
    </div>
  );
}

function HeroSlideshow({ images, interval = 4500 }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!images || images.length < 2) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, interval);
    return () => clearInterval(id);
  }, [images, interval]);

  return (
    <div
      data-testid="hero-slideshow"
      className="relative"
      role="region"
      aria-label="Foto's van onze verhuisliften"
    >
      <div className="photo-frame relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden">
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={i === 0 ? "Rode verhuislift IVECO" : "Mobiele aanhanger-verhuislift"}
            data-testid={`hero-slide-${i}`}
            data-active={i === index}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
      </div>

      {/* Unit badge — synced with current slide */}
      <div className="absolute -top-4 -left-4 hidden md:block">
        <div
          data-testid="hero-slide-badge"
          className="px-3 py-1 bg-[#E62E2D] text-white text-xs tracking-[0.25em] font-display transition-all duration-300"
        >
          UNIT 0{index + 1}
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-3 right-3 flex items-center gap-2 z-10">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Foto ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 transition-all duration-300 ${
              i === index ? "w-8 bg-[#E62E2D]" : "w-4 bg-zinc-600 hover:bg-zinc-400"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-[#0a0a0a] px-4 py-4">
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
        {label}
      </div>
      <div className="mt-1 font-display text-lg text-zinc-100">{value}</div>
    </div>
  );
}

function InfoBlock({ title, text }) {
  return (
    <div className="bg-[#0a0a0a] p-7">
      <div className="text-[10px] tracking-[0.3em] text-[#E62E2D] uppercase">
        Info
      </div>
      <h4 className="mt-2 font-display text-2xl">{title}</h4>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{text}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
